<template>
    <div>
        <ValidationObserver ref="observer">
            <form class="grid grid-cols-2">
                <app-fieldset class="col-span-2 mt-5" :label="$t('statements.deductions')">
                    <div class="col-span-2 gap-2 flex flex-col">
                        <table class="w-full text-xs">
                            <tr class="border-b">
                                <th class="p-2 border text-left">{{ $t('invoices.deduction') }}</th>
                                <th class="p-2 border text-right">{{ $t('commons.amountTTC') }}</th>
                                <th class="p-2 border text-right">{{ $t('invoices.priorDeductionAmountTTC') }}</th>
                                <th class="p-2 border text-right bg-blue-200">
                                    <span :title="$t('invoices.deductionAmountTTCTips')">
                                        {{ $t('invoices.deductionAmountTTC') }}
                                    </span>
                                </th>
                                <th class="p-2 border text-right">
                                    {{ $t('invoices.remainingDeductionAmountTTC') }}
                                </th>
                            </tr>
                            <tr class="border-b" v-for="deduction in chargedDeductions">
                                <td class="border p-2">
                                    <span>{{ deduction.object }}</span>
                                </td>
                                <td class="border p-2 text-right">
                                    <span>{{ $n(deduction.amountTTC, currencyFormat) }}</span>
                                </td>
                                <td class="border p-2 text-right bg-blue-100">
                                    <div class="flex justify-between items-center">
                                        <app-button
                                            v-if="deduction.remainingAmountTTC > 0"
                                            size="mini"
                                            variant="borderless"
                                            icon="icon-arrow-right-bold-box"
                                            class="opacity-10 hover:opacity-100"
                                            :title="$t('invoices.copyRemaining')"
                                            @click="copyRemainingAmountToChargedDeductionAmount(deduction)"
                                        ></app-button>
                                        <span v-else></span>
                                        <app-number-link
                                            :show-label="false"
                                            :label="$t('invoices.subAmountTTC')"
                                            v-model="deduction.chargedAmountTTC"
                                            :positiveOnly="false"
                                            format="decimal"
                                            @input="onDeductionAmountChanged(deduction)"
                                            :currency="currencyFormat.currency"
                                        >
                                            <div class="flex gap-1">
                                                <u class="w-full text-right">
                                                    {{ $n(deduction.chargedAmountTTC || 0, currencyFormat) }}
                                                </u>
                                                <span
                                                    :title="$t('invoices.invoicePercentOfQuoteAmount')"
                                                    v-if="deduction.amountTTC && deduction.amountTTC > 0"
                                                    :class="{
                                                        'text-red-500 font-bold':
                                                            deduction.chargedAmountTTC > deduction.amountTTC,
                                                    }"
                                                >
                                                    ({{
                                                        Math.round(
                                                            (deduction.chargedAmountTTC / deduction.amountTTC) * 100,
                                                        )
                                                    }}%)
                                                </span>
                                            </div>
                                        </app-number-link>
                                    </div>
                                </td>
                                <td class="border p-2 text-right">
                                    <span>
                                        {{
                                            deduction.remainingAmountTTC && deduction.chargedAmountTTC
                                                ? $n(
                                                      deduction.remainingAmountTTC - deduction.chargedAmountTTC,
                                                      currencyFormat,
                                                  )
                                                : '-'
                                        }}
                                    </span>
                                    <span
                                        :title="$t('invoices.invoicePercentOfQuoteAmount')"
                                        v-if="
                                            deduction.amountTTC &&
                                            deduction.remainingAmountTTC &&
                                            deduction.chargedAmountTTC
                                        "
                                        :class="{
                                            'text-red-500 font-bold':
                                                deduction.remainingAmountTTC < deduction.chargedAmountTTC,
                                        }"
                                    >
                                        ({{
                                            Math.round(
                                                (deduction.chargedAmountTTC / deduction.remainingAmountTTC) * 100,
                                            )
                                        }}%)
                                    </span>
                                </td>
                            </tr>
                            <tr>
                                <td></td>
                                <td class="border p-2 text-right">{{ $n(totalDeductions, currencyFormat) }}</td>
                                <td class="border p-2 text-right">
                                    {{ $n(totalDeductionPriorRemainingAmountTTC, currencyFormat) }}
                                </td>
                                <td class="border p-2 text-right">
                                    {{ $n(totalChargedDeductions, currencyFormat) }}
                                </td>
                                <td class="border p-2 text-right">
                                    {{ $n(totalDeductionRemainingAmountTTC, currencyFormat) }}
                                </td>
                            </tr>
                        </table>
                    </div>
                </app-fieldset>
                <app-fieldset
                    class="col-span-2 mt-5"
                    :label="$t('invoices.distribution')"
                    icon="icon-arrow-decision-outline"
                >
                    <div class="col-span-2 gap-2 flex flex-col">
                        <table class="w-full border text-xs col-span-2">
                            <tr class="border-b">
                                <th class="p-2 text-left">{{ $t('invoices.toPay') }}</th>
                                <th class="p-2 text-right bg-blue-200">{{ $t('commons.amountTTC') }}</th>
                            </tr>
                            <tr class="border-b">
                                <td class="border p-2" style="width: 70%">
                                    <span>
                                        <b>[{{ $t('invoices.mainCompany') }}]</b>
                                        {{ invoice.emitter?.company?.name }}
                                    </span>
                                </td>
                                <td class="border p-2 text-right bg-blue-200">
                                    {{
                                        $n(
                                            invoice.situationDistribution?.find((line) => line.id === invoice.emitterId)
                                                ?.amountTTC || 0,
                                            currencyFormat,
                                        )
                                    }}
                                </td>
                            </tr>
                            <tr class="border-b" v-for="subcontractor in subcontractors">
                                <td class="border p-2">
                                    <span>{{ subcontractor.name }}</span>
                                </td>
                                <td class="border p-2 text-right bg-blue-200">
                                    <app-number-link
                                        :label="$t('commons.amountTTC')"
                                        :show-label="false"
                                        v-model="subcontractor.amountTTC"
                                        @input="saveSituationDistribution()"
                                        :positiveOnly="false"
                                        format="decimal"
                                        class="underline"
                                    >
                                        <span class="w-full text-right">
                                            {{ $n(subcontractor.amountTTC, currencyFormat) }}
                                        </span>
                                    </app-number-link>
                                </td>
                            </tr>
                        </table>
                    </div>
                </app-fieldset>
                <app-fieldset
                    class="col-span-2 mt-5"
                    :label="$t('invoices.situationAttachment')"
                    icon="icon-file-document-outline"
                >
                    <div class="col-span-2 gap-2 flex flex-col">
                        <div class="flex flex-col gap-2">
                            <app-upload-button
                                ref="situationFileInput"
                                icon="icon-paperclip"
                                :label="
                                    invoice.situationUrl ? $t('invoices.replaceSituation') : $t('invoices.newSituation')
                                "
                                :end-point="`/api/projects/${$route.params.projectId}/invoices/${$route.params.invoiceId}/situationAttachments`"
                                :name="$t('invoices.newSituation')"
                                class="text-sm offline:hidden"
                                @loaded="onSituationLoaded"
                            />
                            <table class="w-full my-5" v-if="invoice.situationUrl">
                                <tr class="border-b hover:bg-gray-200">
                                    <td class="p-2" style="width: 30px">
                                        <a
                                            :href="invoice.situationUrl"
                                            :download="invoice.fileName"
                                            aria-label="download attachment"
                                            target="_blank"
                                        >
                                            <IconDownload width="16" heigth="16" />
                                        </a>
                                    </td>
                                    <td class="p-2 text-left">
                                        <a
                                            :href="invoice.situationUrl"
                                            class="text-sm"
                                            aria-label="view attachment"
                                            target="_blank"
                                        >
                                            {{ invoice.situationFileName }}
                                        </a>
                                    </td>
                                    <td class="p-2" style="width: 30px" v-if="isAdmin">
                                        <app-button
                                            size="mini"
                                            variant="danger"
                                            aria-label="remove attachment"
                                            icon="icon-trash-can-outline"
                                            @click="removeSituationAttachment()"
                                        />
                                    </td>
                                </tr>
                            </table>
                        </div>
                    </div>
                </app-fieldset>
            </form>
        </ValidationObserver>
    </div>
</template>

<script>
import { getMapById, reduceSumOn, sortBy } from '@/services/sanitize.service';
import { getInvoice, getInvoices, updateInvoice } from './invoices.service';
import { combineLatest } from 'rxjs';
import { getBundles } from '@/features/bundles/bundles.service';
import { getCompanies } from '@/features/companies/companies.service';
import { queryProject } from '@/features/projects/projects.service';
import AppUploadButton from '@/components/app-uploadButton/AppUploadButton.vue';
import { confirm } from '@/features/dialogs/dialogs.service';
import AppNumberLink from '@/components/appNumberLink/AppNumberLink.vue';
import { getAmendments } from '@/features/amendments/amendments.service';
import { getDeductions } from '@/features/deductions/deductions.service';
import { getCIEs } from '@/features/cie/CIE.service';
import AppFieldset from '@/components/appFieldset/AppFieldset.vue';
import AppButton from '@/components/appButton/AppButton.vue';
import AppSeparator from '@/components/appSeparator/AppSeparator.vue';
import IconDownload from '@/icons/IconDownload.vue';
import IconArrowDecisionOutline from '@/icons/IconArrowDecisionOutline.vue';
import { buildSubcontractors, getNewSituationDistribution } from '@/features/statements/statements.service';

export default {
    components: {
        IconArrowDecisionOutline,
        IconDownload,
        AppUploadButton,
        AppSeparator,
        AppNumberLink,
        AppButton,
        AppFieldset,
    },
    async created() {
        queryProject(this.$route.params.projectId).then((project) => {
            this.currencyFormat.currency = project.currency;
            this.isAdmin = project.me.allowedFeatures.includes('project_invoices');
            this.project = project;
        });
        this.init();
    },
    watch: {
        $route: function () {
            this.init();
        },
    },
    computed: {
        totalChargedDeductions() {
            return reduceSumOn(this.chargedDeductions, 'chargedAmountTTC');
        },
        totalDeductions() {
            return reduceSumOn(this.chargedDeductions, 'amountTTC');
        },
        totalDeductionPriorRemainingAmountTTC() {
            return reduceSumOn(this.chargedDeductions, 'remainingAmountTTC');
        },
        totalDeductionRemainingAmountTTC() {
            return reduceSumOn(this.chargedDeductions, (item) =>
                item.remainingAmountTTC && item.chargedAmountTTC ? item.remainingAmountTTC - item.chargedAmountTTC : 0,
            );
        },
    },
    methods: {
        init() {
            this.subscriptions = [
                combineLatest([
                    getBundles(this.$route.params.projectId),
                    getCompanies(this.$route.params.projectId),
                    getInvoices(this.$route.params.projectId),
                    getInvoice(this.$route.params.projectId, this.$route.params.invoiceId),
                    getAmendments(this.$route.params.projectId),
                    getDeductions(this.$route.params.projectId),
                    getCIEs(this.$route.params.projectId),
                ]).subscribe(async ([bundles, companies, invoices, invoice, amendments, deductions, cie]) => {
                    const relevantDeductions = deductions.filter(
                        (deduction) => deduction.deductionStatus === 'definitive',
                    );
                    const relevantCie = cie.filter((cie) => cie.quoteStatus && cie.quoteStatus === 'validated');
                    const companyMap = getMapById(companies);
                    const mappedBundles = bundles.map((bundle) => ({
                        ...bundle,
                        company: companyMap[bundle.companyId],
                    }));
                    this.bundleMap = getMapById(mappedBundles);
                    const amountTTCAlreadyDeductedByAmendmentId = invoices
                        .filter(
                            (anInvoice) =>
                                anInvoice.conclusion === 'validated' &&
                                anInvoice.id !== invoice.id &&
                                invoice.emitterId === invoice.id,
                        )
                        .reduce((acc, invoice) => {
                            if (invoice.chargedDeductions) {
                                return [...acc, ...invoice.chargedDeductions];
                            } else {
                                return acc;
                            }
                        }, [])
                        .reduce((acc, distribution) => {
                            if (acc[distribution.id]) {
                                acc[distribution.id] += distribution.chargedAmountTTC;
                            } else {
                                acc[distribution.id] = distribution.chargedAmountTTC;
                            }
                            return acc;
                        }, {});
                    this.invoice = {
                        chargedDeductions: [],
                        ...invoice,
                        emitter: this.bundleMap[invoice.emitterId],
                    };
                    this.bundles = mappedBundles;

                    this.amendments = sortBy(
                        [
                            ...amendments.filter((amendment) => amendment.quoteStatus === 'validated'),
                            ...relevantCie
                                .filter((cie) => cie.bundleId === invoice.emitterId)
                                .map((cie) => ({ ...cie, type: 'cie' })),
                        ],
                        (amendment) => (amendment.code || 'zzz') + amendment.object,
                    );
                    const cieDeduction = relevantCie
                        .map((cie) => ({
                            ...cie,
                            amountTTC:
                                cie.distribution.find((distribution) => distribution.bundleId === invoice.emitterId)
                                    ?.amount || 0,
                        }))
                        .reduce(
                            (acc, cie) => {
                                return { ...acc, amountTTC: (acc.amountTTC += cie.amountTTC) };
                            },
                            { id: 'cie', amountTTC: 0, type: 'cie', object: this.$t('cie.engagedCIE') },
                        );
                    cieDeduction.remainingAmountTTC =
                        cieDeduction.amountTTC - (amountTTCAlreadyDeductedByAmendmentId['cie'] || 0);
                    const garanteeDeduction = relevantDeductions
                        .filter(
                            (deduction) =>
                                deduction.type === 'garanteeDeduction' && deduction.bundleId === invoice.emitterId,
                        )
                        .reduce(
                            (acc, deduction) => {
                                return { ...acc, amountTTC: (acc.amountTTC += deduction.amountTTC) };
                            },
                            {
                                id: 'garanteeDeduction',
                                amountTTC: 0,
                                type: 'garanteeDeduction',
                                object: this.$t('deductions.types.garanteeDeduction'),
                            },
                        );
                    garanteeDeduction.remainingAmountTTC =
                        garanteeDeduction.amountTTC - (amountTTCAlreadyDeductedByAmendmentId['garanteeDeduction'] || 0);
                    const definitivePenaltyDeduction = deductions
                        .filter(
                            (deduction) =>
                                deduction.type === 'penalty' &&
                                deduction.bundleId === invoice.emitterId &&
                                deduction.deductionStatus === 'definitive',
                        )
                        .reduce(
                            (acc, deduction) => {
                                return { ...acc, amountTTC: (acc.amountTTC += deduction.amountTTC) };
                            },
                            {
                                id: 'penalty',
                                amountTTC: 0,
                                type: 'penalty',
                                object: this.$t('invoices.definitivePenalties'),
                            },
                        );
                    definitivePenaltyDeduction.remainingAmountTTC =
                        definitivePenaltyDeduction.amountTTC - (amountTTCAlreadyDeductedByAmendmentId['penalty'] || 0);
                    const otherDeductions = deductions
                        .filter(
                            (deduction) =>
                                deduction.type === 'deduction' &&
                                deduction.bundleId === invoice.emitterId &&
                                deduction.deductionStatus === 'definitive',
                        )
                        .map((deduction) => ({
                            ...deduction,
                            remainingAmountTTC:
                                deduction.amountTTC - (amountTTCAlreadyDeductedByAmendmentId[deduction.id] || 0),
                        }));
                    this.deductions = [...otherDeductions, garanteeDeduction, cieDeduction, definitivePenaltyDeduction];
                    this.chargedDeductions = this.deductions.map((deduction) => ({
                        ...deduction,
                        remainingAmountTTC:
                            deduction.amountTTC - (amountTTCAlreadyDeductedByAmendmentId[deduction.id] || 0),
                        chargedAmountTTC:
                            this.invoice.chargedDeductions.find(
                                (chargedDeduction) => chargedDeduction.id === deduction.id,
                            )?.amountTTC || 0,
                    }));
                    this.subcontractors = await buildSubcontractors(
                        this.$route.params.projectId,
                        this.invoice.emitterId,
                        this.invoice.situationDistribution,
                    );
                }),
            ];
        },
        async saveSituationDistribution() {
            await updateInvoice(this.$route.params.projectId, {
                id: this.invoice.id,
                situationDistribution: this.computeNewSituationDistribution(),
            });
        },
        copyRemainingAmountToChargedDeductionAmount(deduction) {
            deduction.chargedAmountTTC = deduction.remainingAmountTTC;
            this.onDeductionAmountChanged();
        },
        onDeductionAmountChanged() {
            updateInvoice(this.$route.params.projectId, {
                id: this.invoice.id,
                chargedDeductions: this.chargedDeductions
                    .map((chargedDeduction) => ({
                        id:
                            chargedDeduction.type === 'garanteeDeduction'
                                ? 'garanteeDeduction'
                                : chargedDeduction.type === 'cie'
                                ? 'cie'
                                : chargedDeduction.type === 'penalty'
                                ? 'penalty'
                                : chargedDeduction.id,
                        amountTTC: chargedDeduction.chargedAmountTTC,
                    }))
                    .filter((chargedDeduction) => chargedDeduction.amountTTC !== 0),
            });
        },
        onSituationLoaded(attachment) {
            this.invoice.situationUrl = attachment.url;
            this.invoice.situationFileName = attachment.fileName;
        },
        async removeSituationAttachment() {
            if (await confirm(this.$t('commons.confirmMessage'))) {
                this.invoice.situationUrl = null;
                this.invoice.situationFileName = null;
                await updateInvoice(this.$route.params.projectId, {
                    id: this.invoice.id,
                    situationUrl: null,
                    situationFileName: null,
                });
            }
        },
        computeNewSituationDistribution() {
            return getNewSituationDistribution(
                this.invoice,
                this.subcontractors.filter(
                    (line) => line.id !== this.invoice.emitterId && line.amountTTC && line.amountTTC !== 0,
                ),
            );
        },
    },
    data() {
        return {
            amendments: [],
            deductions: [],
            chargedDeductions: [],
            currencyFormat: {
                currency: 'EUR',
                currencyDisplay: 'symbol',
                style: 'currency',
                minimumFractionDigits: 2,
            },
            subcontractors: [],
            subscriptions: [],
            bundleMap: {},
            bundles: [],
            invoice: {
                name: '',
                emitter: null,
                versions: [],
                chargedDeductions: [],
            },
            isAdmin: false,
            project: {},
        };
    },
};
</script>
